import { mixpanelObject } from 'src/utils/analytics';
import { subscriptionPropertySetter } from 'src/utils/analytics/mixpanel/mixpanelHelper';
import {
  getLocalStorage,
  setLocalStorage
} from 'src/utils/browserStorageHandler';
import { getTempUserBearerToken, isAsZee5com } from 'src/utils/common';
import {
  HTTP_HEADER_KEYS,
  HTTP_METHODS,
  HTTP_URL_PARAMS
} from 'src/utils/constants/HttpMethodsConstants';
import { SubscriptionFlowType } from 'src/utils/constants/utilConstants';
import { isConsumptionPaymentJourney } from 'src/utils/ConsumptionHelper/BuyPlanHelper';

import SimpleStore from '../../store/SimpleStore';
import {
  MIXPANEL_SUCCESS,
  MIXPANEL_FAILURE_REASON,
  MIXPANEL_PROMO_CODE_TYPE,
  MIXPANEL_PREPAID_TYPE,
  MIXPANEL_PREPAID_CODE,
  MIXPANEL_PACK_SELECTED,
  MIXPANEL_COST,
  MIXPANEL_TRANSACTION_CURRENCY,
  MIXPANEL_PAYMENT_METHOD,
  MIXPANEL_CURRENT_SUBSCRIPTION,
  MIXPANEL_BILLING_COUNTRY,
  MIXPANEL_BILLING_STATE,
  MIXPANEL_PROMO_CODE,
  MIXPANEL_POP_UP_NAME,
  MIXPANEL_POP_UP_TYPE,
  MIXPANEL_POP_UP_GROUP,
  MIXPANEL_ACTUAL_COST,
  MIXPANEL_IS_UPGRADE,
  MIXPANEL_SELECTION_SOURCE
} from '../../utils/analytics/mixpanel/mixpanelConstants';
import {
  prepaidCodeResultEvent,
  subscriptionCallInitiatedEvent
} from '../../utils/analytics/mixpanel/mixpanelEventsHelper';
import {
  AUTH_BASE_URL,
  DISPLAY_PLANS_URL,
  PLANS_URL,
  PLAN_API_URL,
  PROMO_CODE_API_URL,
  UPGRADEPLAN_API_URL,
  COUNTRY_CODE_LIST,
  PREPAID_CODE_VERIFICATION_API,
  REDEMPTION_CODE_VERIFICATION_API,
  PROMOTION_API_URL_NEW,
  SUBSCRIPTION_PLAN_API,
  NEW_SUBS_UI as PLANS_SELECTION
} from '../../utils/constants/ApiConstants';
import {
  ENTITLEMENT_USER_TYPE,
  CONSUMPTION_JOURNEY_CODE_REDEMPTION_OBJ,
  CONSUMPTION_PAYMENT_JOURNEY_PATH,
  IS_CONSUMPTION_PAYMENT_JOURNEY,
  SETTINGS,
  PAYMENT_JOURNEY_SOURCE
} from '../../utils/constants/localStorageConstants';
import {
  CLP,
  CountryCode,
  ZEE5,
  ZEE5_PLANS_SYSTEM
} from '../../utils/constants/stringConstants';
import { filterGlobalPlans } from '../../utils/SubscriptionHelper/SubscriptionHelper';
import { buildUrlWithQueryParams } from '../../utils/UrlGenerator';
import { httpService } from '../fetch/HttpService';
import {
  Data,
  DisplayPlansResponse,
  Plan
} from '../responseTypes/displayPlansResponse';
import {
  PlanMetaDataResponse,
  PlansMetaDataResponse
} from '../responseTypes/plansMetaDataResponse';
import { SubscriptionApiResponse } from '../responseTypes/subscriptionApiResponse';

export const getAllSubsciptionPlans = async (countryCode: string) => {
  const url = buildUrlWithQueryParams(SUBSCRIPTION_PLAN_API, {
    country: countryCode,
    translation: 'en',
    include_all: 'true'
  });

  const subscription: SubscriptionApiResponse[] =
    await httpService.fetchApiData({
      url,
      type: HTTP_METHODS.GET
    });
  if (!subscription) return null;
  return subscription;
};
export const getActiveSubscription = async (countryCode: string) => {
  const url = buildUrlWithQueryParams(SUBSCRIPTION_PLAN_API, {
    country: countryCode,
    translation: 'en',
    include_all: 'true'
  });

  let subscription: SubscriptionApiResponse[];
  try {
    subscription = await httpService.fetchApiData({
      url,
      type: HTTP_METHODS.GET
    });
  } catch (error) {
    // httpService.logApiError(error);
    return null;
  }
  if (!subscription) return null;
  // Get Active plan
  const subscriptionActiveList =
    subscription?.length > 0 &&
    subscription.filter((item: SubscriptionApiResponse) => {
      return item.state === 'activated';
    });
  const currentActiveSubscriptions =
    subscriptionActiveList &&
    subscriptionActiveList.filter(
      (item: SubscriptionApiResponse) =>
        getPackStatus(item.subscription_end) === 'Active'
    );
  subscriptionPropertySetter(mixpanelObject, subscription);
  if (currentActiveSubscriptions && currentActiveSubscriptions.length > 0) {
    const maxPricePack = getMaxPricePack([...currentActiveSubscriptions]);
    return { currentActiveSubscriptions, maxPricePack };
  }
  return null;
};

export const getPackStatus = (receivedDate: Date) => {
  let status = 'Expired';
  if (receivedDate) {
    let changedDate;
    const packExpiryDate = new Date(receivedDate);
    const userTimezoneOffset = packExpiryDate.getTimezoneOffset() * 60000;
    if (userTimezoneOffset < 0) {
      changedDate = new Date(packExpiryDate.getTime() + userTimezoneOffset);
    } else {
      changedDate = new Date(packExpiryDate.getTime() - userTimezoneOffset);
    }
    const today = new Date();
    if (changedDate > today) {
      status = 'Active';
    }
  }
  return status;
};

const getMaxPricePack = (
  currentActiveSubscriptions: SubscriptionApiResponse[]
) => {
  let maxPricePlan = currentActiveSubscriptions.pop();
  if (currentActiveSubscriptions.length) {
    currentActiveSubscriptions.forEach((activeSubscription) => {
      if (
        activeSubscription.subscription_plan.price >
        (maxPricePlan?.subscription_plan?.price || 0)
      ) {
        maxPricePlan = activeSubscription;
      }
    });
  }
  return maxPricePlan;
};

export const applyPromoCode = async (
  promoCodeValue: string,
  countryCode: string
) => {
  const lang = getLocalStorage(SETTINGS).display_language || 'en';
  const url = buildUrlWithQueryParams(PROMO_CODE_API_URL + promoCodeValue, {
    translation: lang,
    country: countryCode
  });
  try {
    return await httpService.fetchApiData({
      url,
      type: HTTP_METHODS.GET
    });
  } catch (error) {
    httpService.logApiError(error);
    return null;
  }
};

/**
 * Calling new promotion api
 * @param promoCodeValue promo/prepaid code
 * @param redemption query params need to pass for redemption api call
 */
export const applyPromoCodeNew = async (
  promoCodeValue: string,
  redemption = false
) => {
  const headers = [HTTP_HEADER_KEYS.X_ACCESS_TOKEN];
  const url = buildUrlWithQueryParams(PROMOTION_API_URL_NEW, {
    coupon_code: promoCodeValue,
    country_code: SimpleStore.get(HTTP_URL_PARAMS.COUNTRY_CODE),
    redemption
  });
  try {
    return await httpService.fetchApiData({
      url,
      type: HTTP_METHODS.GET,
      body: {},
      headers
    });
  } catch (error) {
    httpService.logApiError(error);
    return null;
  }
};

export const getCountryList = async () => {
  try {
    return await httpService.fetchApiData({
      url: COUNTRY_CODE_LIST,
      type: HTTP_METHODS.GET
    });
  } catch (error) {
    return await httpService.logApiError(error);
  }
};

export const getUpgradePlansMetaData = async (plans: Plan[]) => {
  const headers = [HTTP_HEADER_KEYS.X_ACCESS_TOKEN];
  const plansMetaData: PlanMetaDataResponse[] = await Promise.all(
    plans
      .filter((plan: Plan) => plan.purchase_allowed)
      .map((plan: Plan) => {
        //adding the country param to the plans api
        const url = buildUrlWithQueryParams(PLAN_API_URL + plan.id, {
          country: SimpleStore.get(HTTP_URL_PARAMS.COUNTRY_CODE)
        });
        return httpService.fetchApiData({
          url,
          type: HTTP_METHODS.GET,
          headers
        });
      })
  );
  return plansMetaData.map((plan: PlanMetaDataResponse) => plan.data);
};

export const getUpgradePlansData = async (
  currentSubscriptionId: string,
  combo_plan_id?: string
) => {
  const countryCode = SimpleStore.get(HTTP_URL_PARAMS.COUNTRY_CODE);
  const headers = [HTTP_HEADER_KEYS.X_ACCESS_TOKEN];
  const url = buildUrlWithQueryParams(
    UPGRADEPLAN_API_URL + currentSubscriptionId,
    {
      country: countryCode,
      combo_plan_id: combo_plan_id,
      system: ZEE5
    }
  );
  let upgradePlansResponse: DisplayPlansResponse;
  try {
    upgradePlansResponse = await httpService.fetchApiData({
      url,
      type: HTTP_METHODS.GET,
      headers
    });
  } catch (error) {
    httpService.logApiError(error);
    return null;
  }
  if (upgradePlansResponse && upgradePlansResponse.data) {
    if (countryCode != 'IN') {
      upgradePlansResponse.data.plans = filterGlobalPlans(
        upgradePlansResponse.data.plans
      );
    }
    upgradePlansResponse.data.plans = upgradePlansResponse.data.plans.sort(
      (a: Plan, b: Plan) => a.priority - b.priority
    );
    return upgradePlansResponse.data;
  }
  return null;
};

export const getDisplayPlansMetaData = async () => {
  const headers = [HTTP_HEADER_KEYS.X_ACCESS_TOKEN];
  const url = buildUrlWithQueryParams(PLANS_URL, {
    country: SimpleStore.get(HTTP_URL_PARAMS.COUNTRY_CODE),
    system: isAsZee5com() ? CLP : ZEE5
    // need to pass system as CLP for tapp flow
  });
  const plansResponse: PlansMetaDataResponse = await httpService.fetchApiData({
    url,
    type: HTTP_METHODS.GET,
    headers
  });
  return plansResponse?.data?.plans || [];
};

export const getDisplayPlansData = async (
  timeout = 8 * 1000,
  id?: string,
  code?: string,
  name?: string
): Promise<Data | null> => {
  const headers = [HTTP_HEADER_KEYS.X_ACCESS_TOKEN];
  const displayPlansParams: {
    country: string;
    system: string;
    id?: string;
    code?: string;
    name?: string;
  } = {
    country: SimpleStore.get(HTTP_URL_PARAMS.COUNTRY_CODE),
    system: isAsZee5com() ? CLP : ZEE5
    // need to pass system as CLP for tapp flow
  };

  if (id && code && name) {
    displayPlansParams.id = id;
    displayPlansParams.code = code;
    displayPlansParams.name = name;
  }
  const url = buildUrlWithQueryParams(DISPLAY_PLANS_URL, displayPlansParams);
  let displayPlansResponse: DisplayPlansResponse;
  try {
    displayPlansResponse = await httpService.fetchApiData({
      url,
      type: HTTP_METHODS.GET,
      headers,
      timeout
    });
    if (displayPlansResponse && displayPlansResponse.data) {
      if (SimpleStore.get(HTTP_URL_PARAMS.COUNTRY_CODE) != 'IN') {
        displayPlansResponse.data.plans = filterGlobalPlans(
          displayPlansResponse.data.plans
        );
      }
      displayPlansResponse.data.plans = displayPlansResponse.data.plans.sort(
        (a: Plan, b: Plan) => a.priority - b.priority
      );
      return displayPlansResponse.data;
    }
  } catch (error) {
    httpService.logApiError(error);
    console.error('err', error);
    return null;
  }
  return null;
};

interface applyPrepaidCodePropsTypes {
  promoCode: string;
  countryCode: string;
  stateCode: string;
  currentSubscription?: SubscriptionApiResponse | null;
  consumptionBuyPlan: boolean;
}
export const applyPrepaidCode = async (
  applyPrepaidCodeProps: applyPrepaidCodePropsTypes
) => {
  const {
    promoCode,
    countryCode,
    stateCode,
    currentSubscription = null,
    consumptionBuyPlan
  } = applyPrepaidCodeProps;
  try {
    const prepaidCodeData = await verifyPrepaidCode(promoCode);

    if (prepaidCodeData && prepaidCodeData.code === 200) {
      const prepaidPlanDetails = prepaidCodeData?.response?.plan_details[0];
      const id = prepaidPlanDetails && prepaidPlanDetails.id;
      prepaidcodeMixpanelCall(
        prepaidPlanDetails,
        countryCode,
        stateCode,
        currentSubscription
      );
      const redemptionData = await redemPrepaidCoupon(promoCode, id);
      if (redemptionData && redemptionData.code === 200) {
        if (consumptionBuyPlan) {
          // set indicator in localstorage for consumption buy plan journey
          setLocalStorage(IS_CONSUMPTION_PAYMENT_JOURNEY, true);
          setLocalStorage(
            CONSUMPTION_PAYMENT_JOURNEY_PATH,
            window.location.pathname
          );
          setLocalStorage(
            CONSUMPTION_JOURNEY_CODE_REDEMPTION_OBJ,
            redemptionData
          );
        }
        mixpanelObject.setProperty(MIXPANEL_SUCCESS, true);
        mixpanelObject.setProperty(MIXPANEL_FAILURE_REASON, 'N/A');
        mixpanelObject.setProperty(
          MIXPANEL_PROMO_CODE_TYPE,
          MIXPANEL_PREPAID_TYPE
        );
        mixpanelObject.setProperty(MIXPANEL_PREPAID_CODE, promoCode);
        prepaidCodeResultEvent();
        window.location.href = '/paymentsuccess?prepaidsuccess=true';
        return false;
      } else {
        mixpanelObject.setProperty(MIXPANEL_SUCCESS, false);
        mixpanelObject.setProperty(
          MIXPANEL_FAILURE_REASON,
          redemptionData.message || 'Redemption failed'
        );
        mixpanelObject.setProperty(
          MIXPANEL_PROMO_CODE_TYPE,
          MIXPANEL_PREPAID_TYPE
        );
        mixpanelObject.setProperty(MIXPANEL_PREPAID_CODE, promoCode);
        prepaidCodeResultEvent();
        return true;
      }
    } else {
      mixpanelObject.setProperty(MIXPANEL_SUCCESS, false);
      mixpanelObject.setProperty(
        MIXPANEL_FAILURE_REASON,
        'Invalid Coupon code'
      );
      mixpanelObject.setProperty(
        MIXPANEL_PROMO_CODE_TYPE,
        MIXPANEL_PREPAID_TYPE
      );
      mixpanelObject.setProperty(MIXPANEL_PREPAID_CODE, promoCode);
      prepaidCodeResultEvent();
      return true;
    }
  } catch (error) {
    httpService.logApiError(error);
    return true;
  }
};

/**
 * Calling new promotion api for redeeming the prepaid code
 * @param promoCodeValue prepaid code
 */
export const redeemPrepaidCode = async (promoCode: string) => {
  try {
    const redemptionData = await applyPromoCodeNew(promoCode, true);
    if (redemptionData && redemptionData.code === 200) {
      mixpanelObject.setProperty(MIXPANEL_SUCCESS, true);
      mixpanelObject.setProperty(MIXPANEL_FAILURE_REASON, 'N/A');
      mixpanelObject.setProperty(
        MIXPANEL_PROMO_CODE_TYPE,
        MIXPANEL_PREPAID_TYPE
      );
      mixpanelObject.setProperty(MIXPANEL_PREPAID_CODE, promoCode);
      prepaidCodeResultEvent();
      window.location.href = '/paymentsuccess?prepaidsuccess=true';
      return false;
    } else {
      mixpanelObject.setProperty(MIXPANEL_SUCCESS, false);
      mixpanelObject.setProperty(
        MIXPANEL_FAILURE_REASON,
        redemptionData.message || 'Redemption failed'
      );
      mixpanelObject.setProperty(
        MIXPANEL_PROMO_CODE_TYPE,
        MIXPANEL_PREPAID_TYPE
      );
      mixpanelObject.setProperty(MIXPANEL_PREPAID_CODE, promoCode);
      prepaidCodeResultEvent();
      return true;
    }
  } catch (error) {
    console.error('plans err =>', error);
    return true;
  }
};

export const prepaidcodeMixpanelCall = (
  prepaidPlanDetails: any,
  countrycode: string,
  statecode: string,
  currentSubscription: SubscriptionApiResponse | null
) => {
  const isPrepaidPlanObject = Object.keys(prepaidPlanDetails).length;
  if (isPrepaidPlanObject) {
    const {
      axinom_plan_id,
      title,
      subscription_plan_type,
      currency,
      discount
    } = prepaidPlanDetails;
    const entitlmentUserType = getLocalStorage(ENTITLEMENT_USER_TYPE);
    mixpanelObject.setProperty(
      MIXPANEL_PACK_SELECTED,
      `${axinom_plan_id}_${title}_${subscription_plan_type}` || 'N/A'
    );
    mixpanelObject.setProperty(
      MIXPANEL_COST,
      (discount
        ? Math.abs(prepaidPlanDetails.price - discount)
        : prepaidPlanDetails.price) || 'N/A'
    );
    mixpanelObject.setProperty(
      MIXPANEL_TRANSACTION_CURRENCY,
      currency || 'N/A'
    );
    mixpanelObject.setProperty(MIXPANEL_PAYMENT_METHOD, 'prepaidcode');
    mixpanelObject.setProperty(MIXPANEL_CURRENT_SUBSCRIPTION, false);
    mixpanelObject.setProperty(MIXPANEL_BILLING_COUNTRY, countrycode);
    mixpanelObject.setProperty(MIXPANEL_BILLING_STATE, statecode);
    mixpanelObject.setProperty(
      MIXPANEL_PROMO_CODE,
      mixpanelObject.getProperty(MIXPANEL_PROMO_CODE)
    );
    mixpanelObject.setProperty(MIXPANEL_PROMO_CODE_TYPE, MIXPANEL_PREPAID_TYPE);
    mixpanelObject.setProperty(MIXPANEL_POP_UP_NAME, 'N/A');
    mixpanelObject.setProperty(MIXPANEL_POP_UP_TYPE, 'N/A');
    mixpanelObject.setProperty(MIXPANEL_POP_UP_GROUP, 'N/A');
    mixpanelObject.setProperty(
      MIXPANEL_ACTUAL_COST,
      prepaidPlanDetails.price || 'N/A'
    );
    mixpanelObject.setProperty(
      MIXPANEL_IS_UPGRADE,
      entitlmentUserType !== 'SVOD' &&
        currentSubscription &&
        Object.keys(currentSubscription).length > 0
    );
    if (isConsumptionPaymentJourney()) {
      mixpanelObject.setProperty(
        MIXPANEL_SELECTION_SOURCE,
        getLocalStorage(PAYMENT_JOURNEY_SOURCE)
      );
    }

    subscriptionCallInitiatedEvent();
  }
};

export const verifyPrepaidCode = async (promoCodeValue: string) => {
  const headers = [HTTP_HEADER_KEYS.X_ACCESS_TOKEN];
  const lang = getLocalStorage(SETTINGS)?.display_language || 'en';
  const url = buildUrlWithQueryParams(PREPAID_CODE_VERIFICATION_API, {
    coupon_code: promoCodeValue,
    translation: lang,
    country_code: SimpleStore.get(HTTP_URL_PARAMS.COUNTRY_CODE)
  });
  try {
    return await httpService.fetchApiData({
      url,
      type: HTTP_METHODS.GET,
      headers
    });
  } catch (error) {
    httpService.logApiError(error);
    return null;
  }
};

export const redemPrepaidCoupon = async (
  promoCodeValue: string,
  id: string
) => {
  const headers = [HTTP_HEADER_KEYS.X_ACCESS_TOKEN];
  const lang = getLocalStorage(SETTINGS).display_language || 'en';
  const url = buildUrlWithQueryParams(REDEMPTION_CODE_VERIFICATION_API, {
    coupon_code: promoCodeValue,
    translation: lang,
    country_code: SimpleStore.get(HTTP_URL_PARAMS.COUNTRY_CODE),
    id: id
  });
  try {
    return await httpService.fetchApiData({
      url,
      type: HTTP_METHODS.GET,
      headers,
      tempToken: getTempUserBearerToken()
    });
  } catch (error) {
    httpService.logApiError(error);
    return null;
  }
};

export const getSubscriptionPlansSelectionData = async ({
  code,
  referralId,
  discountType,
  withAuth,
  timeout = 8_000,
  campaignId,
  isAsZee5
}: {
  code?: string;
  referralId?: string;
  discountType?: string;
  withAuth?: boolean;
  timeout?: number;
  campaignId?: string;
  isAsZee5?: boolean;
}) => {
  const country = SimpleStore.get(HTTP_URL_PARAMS.COUNTRY_CODE);
  const displayLanguage = SimpleStore.get(HTTP_URL_PARAMS.DISPLAY_LANGUAGE);
  const isUS = country === CountryCode.US;
  const url = buildUrlWithQueryParams(PLANS_SELECTION, {
    type: SubscriptionFlowType.SUBSCRIPTION,
    country,
    language: displayLanguage,
    system: (isAsZee5 || isAsZee5com()) && isUS ? CLP : ZEE5_PLANS_SYSTEM,
    'referral-id': referralId,
    'discount-type': discountType,
    'platform-code': 'web_app',
    code,
    'campaign-id': campaignId
  });
  const headers = [HTTP_HEADER_KEYS.X_ACCESS_TOKEN];
  if (withAuth) {
    headers.push(HTTP_HEADER_KEYS.AUTHORIZATION);
  }
  try {
    return await httpService.fetchApiData({
      url,
      type: HTTP_METHODS.GET,
      headers,
      timeout
    });
  } catch (error) {
    httpService.logApiError(error);
    return null;
  }
};
export const getUpgradePlansSelectionData = async ({
  code,
  referralId,
  discountType,
  planId,
  timeout = 8_000,
  campaignId
}: {
  code?: string;
  referralId?: string;
  discountType?: string;
  planId: string;
  timeout?: number;
  campaignId?: string;
}) => {
  const country = SimpleStore.get(HTTP_URL_PARAMS.COUNTRY_CODE);
  const displayLanguage = SimpleStore.get(HTTP_URL_PARAMS.DISPLAY_LANGUAGE);
  const url = buildUrlWithQueryParams(PLANS_SELECTION, {
    type: SubscriptionFlowType.UPGRADE,
    country,
    language: displayLanguage,
    system: ZEE5_PLANS_SYSTEM,
    'referral-id': referralId,
    'discount-type': discountType,
    'platform-code': 'web_app',
    'plan-id': planId,
    code,
    'campaign-id': campaignId
  });
  const headers = [
    HTTP_HEADER_KEYS.X_ACCESS_TOKEN,
    HTTP_HEADER_KEYS.AUTHORIZATION
  ];
  try {
    return await httpService.fetchApiData({
      url,
      type: HTTP_METHODS.GET,
      headers,

      timeout
    });
  } catch (error) {
    httpService.logApiError(error);
    return null;
  }
};
